import { Link } from '@inertiajs/react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import BlogPostCard, { type BlogCardPost, formatDate } from '@/components/marketing/BlogPostCard'
import BlogSidebar from '@/components/marketing/BlogSidebar'
import { type CategoryNavItem } from '@/components/marketing/BlogCategoryNav'
import type { SharedMarketingProps } from '@/types/marketing'

interface BlogPageProps extends SharedMarketingProps {
  posts: BlogCardPost[]
  categories: CategoryNavItem[]
}

export default function Blog({ posts, categories }: BlogPageProps) {
  const [featured, ...rest] = posts

  return (
    <>
      <AtmosphericHero minHeight="48vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <ScrollReveal>
            <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>Blog</span>
          </ScrollReveal>
          <ScrollReveal delay={0.05}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>Insights for artists, labels &amp; the music business</h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="hero-body mx-auto">
              Guides on music distribution, royalties, streaming platforms, Content ID, and the business
              of getting music heard — for artists, labels, and anyone shipping records to the world.
            </p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      {/* Full-width body — sticky sidebar on the left, articles on the
          right. Stacks vertically on mobile (sidebar collapses to a
          horizontal pill row above the content). */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid gap-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14">
          <BlogSidebar categories={categories} />

          <div>
            {/* Featured post */}
            {featured && (
              <ScrollReveal>
                <Link
                  href={`/guides/${featured.slug}`}
                  prefetch
                  className={`group grid overflow-hidden rounded-3xl border border-white/8 bg-white/[0.03] transition-all hover:border-[#ff6a1a]/25 hover:bg-white/[0.05] ${featured.featured_image_url ? 'md:grid-cols-2' : ''}`}
                >
                  {featured.featured_image_url && (
                    <div className="aspect-[16/10] overflow-hidden bg-white/[0.04] md:aspect-auto">
                      <img
                        src={featured.featured_image_url}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-col justify-center p-8 lg:p-10">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-[#ff6a1a]/10 px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-[#ff6a1a]">
                        {featured.category_name ?? 'Latest'}
                      </span>
                      <span className="text-xs text-white/40">Featured</span>
                    </div>
                    <h2 className="mt-4 text-2xl font-bold leading-tight text-white transition-colors group-hover:text-[#ff6a1a] lg:text-3xl">
                      {featured.title}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-white/60 lg:text-base">{featured.description}</p>
                    <div className="mt-5 flex items-center gap-3 text-xs text-white/40">
                      <time>{formatDate(featured.published_at)}</time>
                      <span aria-hidden="true">·</span>
                      <span>{featured.reading_time}</span>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            )}

            {/* Article grid */}
            {rest.length > 0 && (
              <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {rest.map((post, i) => (
                  <ScrollReveal key={post.slug} delay={i * 0.05}>
                    <BlogPostCard post={post} />
                  </ScrollReveal>
                ))}
              </div>
            )}

            {posts.length === 0 && (
              <p className="text-center text-white/48">No articles published yet — check back soon.</p>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-6 py-24">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white">Ready to distribute your music?</h2>
            <p className="mt-3 text-white/64">
              Start your application and get your music on 100+ platforms. Plans from $15/year.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="/apply" className="marketing-button-primary">Apply Now</a>
              <a href="/pricing" className="marketing-button-secondary">See Plans</a>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
